import * as React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  fractionLatex,
  joinParts,
  prefixOf,
  splitParts,
  unitOf,
  type AnswerFormat,
  type UnitHint,
  type VarPrefix,
} from "@/lib/answer-format";

/**
 * Renders what the student typed as a real fraction, so "1/3" is confirmed
 * back to them as ⅓ before they check the answer.
 */
export function FractionPreview({ value, className }: { value: string; className?: string }) {
  const html = React.useMemo(() => {
    const latex = fractionLatex(value);
    if (!latex) return null;
    try {
      return katex.renderToString(latex, { throwOnError: false, displayMode: false });
    } catch {
      return null;
    }
  }, [value]);

  if (!html) return null;

  return (
    <span
      aria-hidden
      title="Taip atrodo tavo trupmena"
      className={cn(
        "inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-blue-700",
        "dark:bg-blue-950/50 dark:text-blue-300",
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Static unit label shown next to the field (cm, €, %, …). */
export function UnitLabel({ unit, className }: { unit: string; className?: string }) {
  return (
    <span className={cn("text-sm font-medium text-muted-foreground select-none", className)}>
      {unit}
    </span>
  );
}

/**
 * Static "x =" label shown in front of the field, so a child solving an
 * equation types only the number and still hands in a full answer.
 */
export function VarPrefixLabel({ prefix, className }: { prefix: VarPrefix; className?: string }) {
  return (
    <span className={cn("text-base text-muted-foreground select-none", className)}>
      <span className="font-serif italic">{prefix.name}</span> =
    </span>
  );
}

/** Unit label + fraction preview — for inline (fill-the-blank) inputs. */
export function AnswerAffix({
  value,
  format,
  className,
}: {
  value: string;
  format?: AnswerFormat | null;
  className?: string;
}) {
  const unit = unitOf(format);
  if (!unit && !fractionLatex(value)) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 ml-1 align-middle", className)}>
      {unit && <UnitLabel unit={unit.unit} />}
      <FractionPreview value={value} className="text-sm" />
    </span>
  );
}

interface AnswerInputProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value: string;
  onValueChange: (value: string) => void;
  /** Shape of the expected answer: unit label, compound fields or a clock. */
  format?: AnswerFormat | null;
  /** Classes for the wrapper row (the input keeps `className`). */
  wrapperClassName?: string;
}

/**
 * Free-form answer field.
 *
 * - plain answer  → one field
 * - "12 cm"       → one field with the unit beside it (appended on check)
 * - "x = 8"       → one field with "x =" in front of it (prepended on check)
 * - "1 val 30 min"→ one field per part, each with its own unit
 * - "3:45"        → hours and minutes fields separated by ":"
 *
 * Fraction-shaped input is echoed back as a rendered trupmena.
 */
export function AnswerInput({
  value,
  onValueChange,
  format,
  className,
  wrapperClassName,
  ...props
}: AnswerInputProps) {
  if (format && format.kind !== "value") {
    return (
      <MultiPartAnswerInput
        {...props}
        value={value}
        onValueChange={onValueChange}
        format={format}
        className={className}
        wrapperClassName={wrapperClassName}
      />
    );
  }

  const unit = unitOf(format);
  const prefix = prefixOf(format);
  return (
    <span className={cn("inline-flex items-center gap-2 flex-wrap", wrapperClassName)}>
      {prefix && <VarPrefixLabel prefix={prefix} />}
      <Input
        {...props}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(unit || prefix ? "w-32" : "max-w-xs", className)}
      />
      {unit && <UnitLabel unit={unit.unit} />}
      <FractionPreview value={value} />
    </span>
  );
}

/**
 * One field per part of a compound answer. The assembled answer ("1 val 30 min",
 * "3:45") is what the parent stores, while each field keeps exactly the digits
 * the student typed — nothing is reformatted under the cursor.
 */
function MultiPartAnswerInput({
  value,
  onValueChange,
  format,
  className,
  wrapperClassName,
  ref,
  ...props
}: Omit<AnswerInputProps, "format"> & { format: AnswerFormat }) {
  const isClock = format.kind === "clock";
  const labels: (UnitHint | null)[] = isClock
    ? [null, null]
    : format.kind === "compound"
      ? format.parts
      : [null];

  const [parts, setParts] = React.useState<string[]>(() => splitParts(value, format));
  // What this component last sent upwards — anything else came from outside
  // (a restored answer) and must be pulled back into the fields.
  const emitted = React.useRef(value);
  const partRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setParts(splitParts(value, format));
    }
  }, [value, format]);

  function updatePart(index: number, partValue: string) {
    const next = labels.map((_, i) => (i === index ? partValue : parts[i] || ""));
    setParts(next);
    const assembled = joinParts(next, format);
    emitted.current = assembled;
    onValueChange(assembled);
  }

  return (
    <span className={cn("inline-flex items-center gap-1.5 flex-wrap", wrapperClassName)}>
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          {isClock && i > 0 && <span className="text-lg font-semibold px-0.5">:</span>}
          <Input
            {...props}
            ref={(el) => {
              partRefs.current[i] = el;
              // The first field stands in for the whole answer (auto-focus).
              if (i !== 0 || !ref) return;
              if (typeof ref === "function") ref(el);
              else ref.current = el;
            }}
            value={parts[i] || ""}
            inputMode="numeric"
            placeholder={isClock ? (i === 0 ? "val." : "min.") : ""}
            onChange={(e) => updatePart(i, e.target.value)}
            onKeyDown={(e) => {
              // Enter walks through the parts before checking the answer.
              if (e.key === "Enter" && i < labels.length - 1 && parts[i]?.trim()) {
                e.preventDefault();
                partRefs.current[i + 1]?.focus();
                return;
              }
              props.onKeyDown?.(e);
            }}
            className={cn("w-16 text-center", className)}
          />
          {label && <UnitLabel unit={label.unit} />}
        </React.Fragment>
      ))}
    </span>
  );
}
